import React, {
    useContext, useCallback, useState, useEffect
} from 'react';
import _ from 'lodash';
import { ModalNextProvider } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
import { MockUpUtil } from 'gw-portals-util-js';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import metadata from './DriverAssignmentPage.metadata.json5';
import messages from './DriverAssignmentPage.messages';
import { PolicyChange, messages as commonMessage } from 'gw-capability-policychange-common-react';

function DriverAssignmentPage(props) {
    const { wizardData, updateWizardData } = props;
    const { submissionVM } = wizardData;
    const { EndorsementService } = useDependencies('EndorsementService');
    const viewModelService = useContext(ViewModelServiceContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const translator = useContext(TranslatorContext);
    const [isPageInitialised, updateIsPageInitialised] = useState(false);

    const { authHeader } = useAuthentication();
    const {
        onValidate,
        initialValidation,
        isComponentValid,
        registerComponentValidation,
        registerInitialComponentValidation
    } = useValidation('DriverAssignmentPage');

    useEffect(() => {
        if (!isPageInitialised) {            
            const vehiclesPath = 'lobData.businessAuto.coverables.vehicles';
            const vehicles = _.get(submissionVM, `${vehiclesPath}.value`);

            const newVehicles = vehicles.map((vehicle, index) => {
                const pathToCheckForMockData = `${vehiclesPath}.${index}`;
                const isLincenseNumberMocked = MockUpUtil.isValueMocked(
                    submissionVM.value,
                    'quote.ba',
                    `${pathToCheckForMockData}.license`
                );
                const isVINMocked = MockUpUtil.isValueMocked(
                    submissionVM.value,
                    'quote.ba',
                    `${pathToCheckForMockData}.vin`
                );
                const newVehicle = {
                    ...vehicle,
                    vin: isVINMocked ? undefined : vehicle.vin,
                    costNew: isLincenseNumberMocked
                        ? {
                            ...vehicle.costNew,
                            amount: undefined
                        }
                        : vehicle.costNew
                };
                if (isLincenseNumberMocked && newVehicle.license) {
                    delete newVehicle.license;
                }
                return newVehicle;
            });

            if (!_.isEqual(newVehicles, vehicles)) {
                _.set(submissionVM, `${vehiclesPath}.value`, newVehicles);
                wizardData.submissionVM = submissionVM;
                updateWizardData(wizardData);
            }
            updateIsPageInitialised(true);
        }
    }, [isPageInitialised, submissionVM, updateWizardData]);

    const onNext = useCallback(async () => {        
        if (!submissionVM.value.validate()) {
            updateWizardData(wizardData);
            return false;
        }
        submissionVM.value.beforeSave();
        const policyChangeResponse = await EndorsementService.saveEndorsement(
            [submissionVM.value],
            authHeader
        );
        submissionVM.value = new PolicyChange(policyChangeResponse);
        return wizardData;
    }, [EndorsementService, authHeader, submissionVM, wizardData, updateWizardData]);

    const updateSubmission = (_submissionVM) => {
        var newWizardData = _.cloneDeep(wizardData);
        const newSubmissionVMClone = viewModelService.clone(_submissionVM);
        newWizardData.submissionVM = newSubmissionVMClone;  
        updateWizardData(newWizardData);
    }

    const generateOverrides = useCallback(() => {
        const vehicles = _.get(submissionVM, 'lobData.businessAuto.coverables.vehicles.value', []);

        const overrides = vehicles.map((vehicle, index) => {

            return {
                [`DriverAssignment${index}`]: {
                     submissionVM: submissionVM,
                     isPolicyChange: true,
                     vehicleID: vehicle.fixedId,
                     updateWizardData: updateSubmission
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [submissionVM, translator]);

    const overrideProps = {
        '@field': {
            showOptional: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
        },
        ...generateOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate
        }
    };

    const isSubmissionQuoted = useCallback(() => {
        return _.get(submissionVM.value, 'baseData.periodStatus') === 'Quoted';
    }, [submissionVM]);

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps);
        },
        [submissionVM, overrideProps]
    );

    useEffect(() => {
        registerInitialComponentValidation(isSubmissionQuoted);
    }, [isSubmissionQuoted, registerInitialComponentValidation]);

    const handleModelChange = useCallback(
        (newModel) => {
            updateWizardData({
                ...wizardData,
                submissionVM: newModel
            });
        },
        [updateWizardData, wizardData]
    );

    if (!isPageInitialised) {
        return null;
    }

    return (
        <WizardPage onNext={onNext} skipWhen={initialValidation} disableNext={!(submissionVM.aspects.valid && submissionVM.lobData.businessAuto.coverables.vehicleDrivers.aspects.subtreeValid)}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                onModelChange={handleModelChange}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                resolveValue={readValue}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </WizardPage>
    );
}

DriverAssignmentPage.propTypes = wizardProps;
export default DriverAssignmentPage;
